import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import { isAccountPrimaryRuntime, isTauriRuntime, isWebRuntime } from "../../lib/runtime";
import { userFacingError } from "../../../shared/userFacingError";
import { cloudAuthDesktopV2, signInForCurrentRuntime } from "../../services/cloudAuthDesktopV2";
import { useDesktopFeatureFlag } from "../../hooks/useDesktopFeatureFlag";
import {
  clearUserPromptFromStorage,
  readUserPromptFromStorage,
  writeUserPromptToStorage,
  type UserPromptKind,
} from "../../../shared/masterPrompt";
import {
  ANTHROPIC_CHAT_MODELS,
  OPENAI_CHAT_MODELS,
  type LlmModelValue,
} from "../../../shared/llmModels";
import {
  readEditorWorkspaceRoot,
  writeEditorWorkspaceRoot,
} from "../../../shared/editorWorkspace";
import type { ThemeMode } from "../../../shared/uiThemes";
import { DARK_PRESETS, LIGHT_PRESETS } from "../../../shared/uiThemes";
import {
  showClientsRoster,
  type WorkbenchPersona,
} from "../../../shared/personaMode";
import type { BookAudioSettings } from "../../../shared/bookAudioSettings";
import { normalizeWritingGoalWords } from "../../../shared/models";
import {
  BOOK_TYPE_LABELS,
  BOOK_TYPES,
  type BookStructure,
  type BookTypeId,
} from "../../../shared/bookType";
import {
  formatVersionLabel,
  readBrowserVersionInfo,
  type AppVersionInfo,
} from "../../utils/appVersion";
import {
  DESKTOP_ELEVENLABS_VOICES,
  formatCustomElevenLabsVoiceLabel,
  looksLikeElevenLabsVoiceId,
} from "../../../shared/desktopCloudTts";
import { ClientsPanel } from "./ClientsPanel";
import { IconPlay } from "./icons";
import { LlmModelSelect } from "./LlmModelSelect";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { PanelHeader } from "./PanelHeader";
import { EmptyState } from "../ui/empty-state";
import { Card } from "../ui/card";
import { cn } from "../../lib/utils";
import {
  chooseBackupFolder,
  defaultBackupFolder,
  readBackupFolderOverride,
  resolveBackupFolder,
  writeBackupFolderOverride,
} from "../../services/desktopBackup";

type BillingTokens = {
  used: number;
  reserved: number;
  limit: number;
  remaining: number;
  bonusRemaining: number;
};

type BillingReferral = {
  code: string;
  sharePath: string;
  shareUrl: string;
};

function formatTokenCount(n: number): string {
  if (!Number.isFinite(n)) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}k`;
  return String(Math.round(n));
}

type Props = {
  onCloudAuthChange: (signedIn: boolean) => void;
  themeMode: ThemeMode;
  onThemeModeChange: (theme: ThemeMode) => void;
  defaultChatModel: LlmModelValue;
  onDefaultChatModelChange: (model: LlmModelValue, provider: "openai" | "anthropic") => void;
  persona: WorkbenchPersona;
  onPersonaChange: (persona: WorkbenchPersona) => void;
  onOpenBookId: (bookId: string) => Promise<void> | void;
  audioSettings: BookAudioSettings;
  onAudioSettingsChange: (next: BookAudioSettings) => void;
  hasOpenBook: boolean;
  writingGoalWords?: number;
  onWritingGoalChange?: (n: number) => void;
  bookType?: BookTypeId;
  bookStructure?: BookStructure;
  onBookTypeChange?: (bookType: BookTypeId, structure?: BookStructure) => void;
  openaiApiKeyDraft?: string;
  anthropicApiKeyDraft?: string;
  onOpenaiApiKeyChange?: (value: string) => void;
  onAnthropicApiKeyChange?: (value: string) => void;
  onSaveApiKeys?: () => void;
  apiKeysSaved?: boolean;
};

type UserPromptDrafts = Record<UserPromptKind, string>;

const EMPTY_USER_PROMPTS: UserPromptDrafts = {
  chat: "",
  variation: "",
  variationStoryCraft: "",
};

function readAllUserPrompts(): UserPromptDrafts {
  return {
    chat: readUserPromptFromStorage("chat"),
    variation: readUserPromptFromStorage("variation"),
    variationStoryCraft: readUserPromptFromStorage("variationStoryCraft"),
  };
}

const USER_PROMPT_FIELDS: {
  kind: UserPromptKind;
  title: string;
  description: string;
  ariaLabel: string;
}[] = [
  {
    kind: "chat",
    title: "Chat prompt",
    description: "Added to the master style guide for assistant chat.",
    ariaLabel: "Chat prompt",
  },
  {
    kind: "variation",
    title: "Variation prompt",
    description: "Added when generating a chapter variation from chat or the variations panel.",
    ariaLabel: "Variation prompt",
  },
  {
    kind: "variationStoryCraft",
    title: "Variation from Story Craft prompt",
    description: "Added when generating a variation from Story Craft checklist items.",
    ariaLabel: "Variation from Story Craft prompt",
  },
];

type SettingsSectionId = "general" | "audio" | "role" | "clients" | "account" | "workspace" | "backup" | "about";

type TtsVoice = { id: string; name: string; provider: "elevenlabs" | "openai" | "local" };

const PROVIDER_ORDER: TtsVoice["provider"][] = ["elevenlabs", "openai", "local"];
const PROVIDER_LABELS: Record<TtsVoice["provider"], string> = {
  elevenlabs: "ElevenLabs",
  openai: "OpenAI",
  local: "Local (Kokoro)",
};

const BASE_SECTIONS: { id: SettingsSectionId; label: string; description: string }[] = [
  { id: "general", label: "General", description: "Theme, model, and prompts" },
  { id: "audio", label: "Audio", description: "Book narration voices" },
  { id: "role", label: "Upgrade", description: "Plans and workbench roles" },
  { id: "account", label: "Account", description: "Sign-in and sync" },
  { id: "backup", label: "Backups", description: "Local backup folder" },
  { id: "workspace", label: "Workspace", description: "Editor client folders" },
  { id: "about", label: "About", description: "This app" },
];

export function SettingsPanel({
  onCloudAuthChange,
  themeMode,
  onThemeModeChange,
  defaultChatModel,
  onDefaultChatModelChange,
  persona,
  onPersonaChange,
  onOpenBookId,
  audioSettings,
  onAudioSettingsChange,
  hasOpenBook,
  writingGoalWords = 0,
  onWritingGoalChange,
  bookType = "fiction",
  bookStructure = "chapters",
  onBookTypeChange,
  openaiApiKeyDraft = "",
  anthropicApiKeyDraft = "",
  onOpenaiApiKeyChange,
  onAnthropicApiKeyChange,
  onSaveApiKeys,
  apiKeysSaved = false,
}: Props) {
  const web = isWebRuntime();
  const accountPrimary = isAccountPrimaryRuntime();
  const tauri = isTauriRuntime();
  const showClients = showClientsRoster(persona) && !web;
  const experimentalBeta = useDesktopFeatureFlag("example.beta_ui");
  const sections = useMemo(() => {
    let list = BASE_SECTIONS.filter((s) => {
      if (web && s.id === "workspace") return false;
      if (!tauri && s.id === "backup") return false;
      return true;
    });
    if (showClients) {
      list = [
        list[0],
        list[1],
        list[2],
        { id: "clients" as const, label: "Clients", description: "Editorial engagements" },
        ...list.slice(3),
      ];
    }
    return list;
  }, [showClients, web, tauri]);

  const [section, setSection] = useState<SettingsSectionId>("general");
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [userLabel, setUserLabel] = useState("");
  const [webCloudConfigured, setWebCloudConfigured] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [userPromptDrafts, setUserPromptDrafts] = useState<UserPromptDrafts>(() => readAllUserPrompts());
  const [userPromptsSaved, setUserPromptsSaved] = useState(false);
  const [workspaceRoot, setWorkspaceRoot] = useState(() => readEditorWorkspaceRoot() ?? "");
  const [workspaceSaved, setWorkspaceSaved] = useState(false);
  const [backupFolder, setBackupFolder] = useState("");
  const [backupSaved, setBackupSaved] = useState(false);
  const [ttsVoices, setTtsVoices] = useState<TtsVoice[]>([]);
  const [ttsError, setTtsError] = useState<string | null>(null);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [previewingVoiceId, setPreviewingVoiceId] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [customVoiceIdDraft, setCustomVoiceIdDraft] = useState("");
  const [customVoiceError, setCustomVoiceError] = useState<string | null>(null);
  const previewAudioRef = useRef<{ audio: HTMLAudioElement; objectUrl: string } | null>(null);
  const [billingInterval, setBillingInterval] = useState<"month" | "year">("month");
  const [versionInfo, setVersionInfo] = useState<AppVersionInfo | null>(() =>
    web ? readBrowserVersionInfo() : null
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const api = window.desktopV2Api;
        if (api?.getVersionInfo) {
          const info = await api.getVersionInfo();
          if (!cancelled) setVersionInfo(info);
          return;
        }
      } catch {
        /* fall through */
      }
      if (!cancelled && web) setVersionInfo(readBrowserVersionInfo());
    })();
    return () => {
      cancelled = true;
    };
  }, [web]);
  const [cloudPlan, setCloudPlan] = useState<"free" | "author" | "editor" | null>(null);
  const [hasStripeBilling, setHasStripeBilling] = useState(false);
  const [billingTokens, setBillingTokens] = useState<BillingTokens | null>(null);
  const [hasPublishPro, setHasPublishPro] = useState(false);
  const [billingReferral, setBillingReferral] = useState<BillingReferral | null>(null);
  const [billingBusy, setBillingBusy] = useState(false);
  const [billingMessage, setBillingMessage] = useState("");
  const [referralCopied, setReferralCopied] = useState(false);

  const applyBillingPlan = useCallback(
    (billing: Awaited<ReturnType<typeof cloudAuthDesktopV2.getBillingPlan>>) => {
      setCloudPlan(billing.plan);
      setHasStripeBilling(billing.hasStripe);
      setBillingTokens(billing.tokens);
      setHasPublishPro(Boolean(billing.entitlements?.publishPro));
      setBillingReferral(billing.referral);
    },
    []
  );

  const refreshState = useCallback(async () => {
    const webConfigured = await cloudAuthDesktopV2.isWebCloudConfigured();
    setWebCloudConfigured(webConfigured);

    if (webConfigured) {
      await cloudAuthDesktopV2.restoreSessionFromStorage();
    }

    if (cloudAuthDesktopV2.isSignedIn()) {
      const email = cloudAuthDesktopV2.getUserEmail();
      setIsSignedIn(true);
      setUserLabel(email ? `${email} (Just Write)` : "Signed in with Just Write");
      onCloudAuthChange(true);
      try {
        const billing = await cloudAuthDesktopV2.getBillingPlan();
        applyBillingPlan(billing);
      } catch {
        setCloudPlan(null);
        setHasStripeBilling(false);
        setBillingTokens(null);
        setHasPublishPro(false);
        setBillingReferral(null);
      }
      return;
    }

    setIsSignedIn(false);
    setUserLabel("");
    setCloudPlan(null);
    setHasStripeBilling(false);
    setBillingTokens(null);
    setHasPublishPro(false);
    setBillingReferral(null);
    onCloudAuthChange(false);
  }, [applyBillingPlan, onCloudAuthChange]);

  useEffect(() => {
    setMessage("");
    setUserPromptDrafts(readAllUserPrompts());
    setUserPromptsSaved(false);
    setWorkspaceRoot(readEditorWorkspaceRoot() ?? "");
    setWorkspaceSaved(false);
    void refreshState();
    if (isTauriRuntime()) {
      void resolveBackupFolder().then(setBackupFolder).catch(() => {
        setBackupFolder(readBackupFolderOverride() ?? "");
      });
    }
  }, [refreshState]);

  useEffect(() => {
    if (section !== "audio") return;
    let cancelled = false;
    setTtsLoading(true);
    setTtsError(null);
    setPreviewError(null);
    void window.desktopV2Api.getChapterAudioTtsProvider().then((res) => {
      if (cancelled) return;
      setTtsLoading(false);
      if (!res.ok || !res.voices?.length) {
        setTtsVoices([]);
        setTtsError(res.error ?? "No TTS voices available.");
        return;
      }
      setTtsVoices(
        res.voices.map((v) => ({
          id: v.id,
          name: v.name,
          provider: v.provider ?? "elevenlabs",
        }))
      );
    });
    return () => {
      cancelled = true;
    };
  }, [section]);

  useEffect(() => {
    return () => {
      const current = previewAudioRef.current;
      if (!current) return;
      current.audio.pause();
      URL.revokeObjectURL(current.objectUrl);
      previewAudioRef.current = null;
    };
  }, []);

  const voicesByProvider = useMemo(() => {
    const catalogIds = new Set(ttsVoices.map((v) => v.id));
    const customElevenLabs: TtsVoice[] = audioSettings.voiceIds
      .filter((id) => !catalogIds.has(id) && looksLikeElevenLabsVoiceId(id))
      .map((id) => ({
        id,
        name: formatCustomElevenLabsVoiceLabel(id),
        provider: "elevenlabs" as const,
      }));

    const groups: Array<{ provider: TtsVoice["provider"]; voices: TtsVoice[] }> = [];
    for (const provider of PROVIDER_ORDER) {
      const voices = [
        ...ttsVoices.filter((v) => v.provider === provider),
        ...(provider === "elevenlabs" ? customElevenLabs : []),
      ];
      if (voices.length) groups.push({ provider, voices });
    }
    // Custom ElevenLabs voices with key unavailable still show if selected on the book.
    if (customElevenLabs.length && !groups.some((g) => g.provider === "elevenlabs")) {
      groups.unshift({ provider: "elevenlabs", voices: customElevenLabs });
    }
    return groups;
  }, [audioSettings.voiceIds, ttsVoices]);

  const hasElevenLabsVoices = ttsVoices.some((v) => v.provider === "elevenlabs");

  const addCustomElevenLabsVoice = useCallback(() => {
    setCustomVoiceError(null);
    const id = customVoiceIdDraft.trim();
    if (!id) {
      setCustomVoiceError("Paste an ElevenLabs voice ID.");
      return;
    }
    if (!looksLikeElevenLabsVoiceId(id)) {
      setCustomVoiceError(
        "That doesn’t look like an ElevenLabs voice ID (16–64 letters and numbers, no spaces)."
      );
      return;
    }
    if (audioSettings.voiceIds.includes(id)) {
      setCustomVoiceError("That voice is already selected for this book.");
      return;
    }
    onAudioSettingsChange({ voiceIds: [...audioSettings.voiceIds, id] });
    setCustomVoiceIdDraft("");
  }, [audioSettings.voiceIds, customVoiceIdDraft, onAudioSettingsChange]);

  const stopVoicePreview = useCallback(() => {
    const current = previewAudioRef.current;
    if (!current) return;
    current.audio.pause();
    URL.revokeObjectURL(current.objectUrl);
    previewAudioRef.current = null;
    setPreviewingVoiceId(null);
  }, []);

  const previewVoice = useCallback(
    async (voice: TtsVoice) => {
      setPreviewError(null);
      stopVoicePreview();
      setPreviewingVoiceId(voice.id);
      try {
        const res = await window.desktopV2Api.previewTtsVoice({ voiceId: voice.id });
        if (!res.ok || !res.audioBase64) {
          throw new Error(res.error ?? "Voice preview failed");
        }
        const binary = atob(res.audioBase64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const objectUrl = URL.createObjectURL(
          new Blob([bytes], { type: res.contentType ?? "audio/mpeg" })
        );
        const audio = new Audio(objectUrl);
        previewAudioRef.current = { audio, objectUrl };
        audio.onended = () => {
          URL.revokeObjectURL(objectUrl);
          if (previewAudioRef.current?.audio === audio) previewAudioRef.current = null;
          setPreviewingVoiceId(null);
        };
        await audio.play();
      } catch (e) {
        stopVoicePreview();
        setPreviewError(userFacingError(e, "generate"));
      }
    },
    [stopVoicePreview]
  );

  const toggleBookVoice = (voiceId: string) => {
    const selected = new Set(audioSettings.voiceIds);
    if (selected.has(voiceId)) selected.delete(voiceId);
    else selected.add(voiceId);
    onAudioSettingsChange({ voiceIds: Array.from(selected) });
  };

  useEffect(() => {
    if (section === "clients" && !showClients) {
      setSection("role");
    }
  }, [section, showClients]);

  useEffect(() => {
    if (section !== "role") return;
    setBillingMessage("");
    if (!cloudAuthDesktopV2.isSignedIn()) return;
    void cloudAuthDesktopV2
      .getBillingPlan()
      .then((billing) => {
        applyBillingPlan(billing);
      })
      .catch(() => {
        /* keep last known */
      });
  }, [section, applyBillingPlan]);

  const onUpgrade = async (plan: "author" | "editor") => {
    setBillingMessage("");
    if (!cloudAuthDesktopV2.isSignedIn()) {
      setBillingMessage("Sign in under Account first, then return here to upgrade.");
      setSection("account");
      return;
    }
    setBillingBusy(true);
    try {
      await cloudAuthDesktopV2.startCheckout(plan, billingInterval);
      setBillingMessage("Checkout opened in your browser. Return here after you finish.");
      onPersonaChange(plan);
    } catch (e) {
      setBillingMessage(userFacingError(e, "billing"));
    } finally {
      setBillingBusy(false);
    }
  };

  const onBuyPublishPro = async () => {
    setBillingMessage("");
    const startPublishPro =
      typeof (cloudAuthDesktopV2 as { startPublishProCheckout?: () => Promise<string> })
        .startPublishProCheckout === "function"
        ? cloudAuthDesktopV2.startPublishProCheckout.bind(cloudAuthDesktopV2)
        : null;
    if (!startPublishPro) {
      setBillingMessage("Publish Pro checkout is not available in this build yet.");
      return;
    }
    if (!cloudAuthDesktopV2.isSignedIn()) {
      setBillingMessage("Sign in under Account first, then return here to buy Publish Pro.");
      setSection("account");
      return;
    }
    setBillingBusy(true);
    try {
      await startPublishPro();
      setBillingMessage("Publish Pro checkout opened in your browser. Return here after you finish.");
    } catch (e) {
      setBillingMessage(userFacingError(e, "billing"));
    } finally {
      setBillingBusy(false);
    }
  };

  const onCopyReferralLink = async () => {
    if (!billingReferral?.shareUrl) return;
    try {
      await navigator.clipboard.writeText(billingReferral.shareUrl);
      setReferralCopied(true);
      window.setTimeout(() => setReferralCopied(false), 2000);
    } catch {
      setBillingMessage("Could not copy referral link.");
    }
  };

  const onManageBilling = async () => {
    setBillingMessage("");
    setBillingBusy(true);
    try {
      await cloudAuthDesktopV2.openBillingPortal();
      setBillingMessage("Billing portal opened in your browser.");
    } catch (e) {
      setBillingMessage(userFacingError(e, "billing"));
    } finally {
      setBillingBusy(false);
    }
  };

  const onPickWorkspace = async () => {
    const api = window.desktopV2Api;
    if (!api.chooseDirectory) {
      setMessage("Update the app to choose a workspace folder.");
      return;
    }
    const result = await api.chooseDirectory({
      title: "Choose editor clients folder",
      defaultPath: workspaceRoot || undefined,
    });
    if (result.canceled || !result.path) return;
    setWorkspaceRoot(result.path);
    writeEditorWorkspaceRoot(result.path);
    setWorkspaceSaved(true);
  };

  const onClearWorkspace = () => {
    setWorkspaceRoot("");
    writeEditorWorkspaceRoot(null);
    setWorkspaceSaved(true);
  };

  const onWebSignIn = async () => {
    setBusy(true);
    setMessage("");
    try {
      await signInForCurrentRuntime();
      if (cloudAuthDesktopV2.isSignedIn()) {
        setMessage("Connected. Your books save to your account when you're online.");
        await refreshState();
      } else if (web) {
        setMessage("Continuing with your Just Write account…");
      }
    } catch (e) {
      setMessage(userFacingError(e, "sign-in"));
    } finally {
      setBusy(false);
    }
  };

  const onSignOut = () => {
    cloudAuthDesktopV2.fullSignOut();
    setIsSignedIn(false);
    setUserLabel("");
    setMessage("You've been signed out.");
    onCloudAuthChange(false);
  };

  const updateUserPromptDraft = (kind: UserPromptKind, value: string) => {
    setUserPromptDrafts((prev) => ({ ...prev, [kind]: value }));
    setUserPromptsSaved(false);
  };

  const onSaveUserPrompts = () => {
    (Object.keys(EMPTY_USER_PROMPTS) as UserPromptKind[]).forEach((kind) => {
      writeUserPromptToStorage(kind, userPromptDrafts[kind]);
    });
    setUserPromptDrafts(readAllUserPrompts());
    setUserPromptsSaved(true);
    setMessage("Prompts saved. They will be added to the built-in style guide on matching requests.");
  };

  const onClearUserPrompts = () => {
    (Object.keys(EMPTY_USER_PROMPTS) as UserPromptKind[]).forEach((kind) => {
      clearUserPromptFromStorage(kind);
    });
    setUserPromptDrafts({ ...EMPTY_USER_PROMPTS });
    setUserPromptsSaved(false);
    setMessage("Author prompts cleared. Only the built-in master style guide will be used.");
  };

  return (
    <section className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden" aria-label="Settings">
      <Card className="m-1 flex h-full min-h-0 flex-1 gap-0 overflow-hidden rounded-lg border border-border bg-card py-0 shadow-sm ring-0">
        <PanelHeader
          title="Settings"
          description="Preferences for Just Write on this device"
        />

        {!experimentalBeta.loading && experimentalBeta.enabled ? (
          <div className="mx-3 mb-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-950">
            Experimental UI is on for your account (example.beta_ui).
          </div>
        ) : null}

        <div className="flex min-h-0 flex-1">
          <nav
            className="flex w-50 shrink-0 flex-col gap-0.5 border-r border-border bg-muted/30 p-2"
            aria-label="Settings sections"
          >
            {sections.map((s) => (
              <Button
                key={s.id}
                type="button"
                variant="ghost"
                onClick={() => setSection(s.id)}
                className={cn(
                  "h-auto flex-col items-stretch justify-start rounded-lg px-3 py-2.5 text-left text-sm transition-colors",
                  section === s.id
                    ? "bg-background font-medium text-foreground shadow-sm ring-1 ring-border"
                    : "text-muted-foreground hover:bg-muted/80 hover:text-foreground"
                )}
              >
                <span className="block leading-tight">{s.label}</span>
                <span className="mt-0.5 block text-[11px] font-normal leading-snug text-muted-foreground">
                  {s.description}
                </span>
              </Button>
            ))}
          </nav>

          <main className="min-h-0 flex-1 overflow-y-auto text-sm leading-relaxed">
            {section === "general" && (
              <div className="flex max-w-2xl flex-col gap-5 p-5">
                <div className="flex flex-col gap-2.5">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Color theme</h3>
                    <p className="max-w-xl text-[0.8125rem] leading-relaxed text-muted-foreground">
                      Choose how JustWrite looks. System follows your OS light or dark preference.
                    </p>
                  </div>
                  <div className="max-w-xs">
                    <Select
                      value={themeMode}
                      onValueChange={(value) => onThemeModeChange(value as ThemeMode)}
                    >
                      <SelectTrigger className="w-full" aria-label="Color theme" size="default">
                        <SelectValue placeholder="Color theme" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="system">System</SelectItem>
                        <SelectGroup>
                          <SelectLabel>Light</SelectLabel>
                          {LIGHT_PRESETS.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel>Dark</SelectLabel>
                          {DARK_PRESETS.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex flex-col gap-2.5">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Default AI model</h3>
                    <p className="max-w-xl text-[0.8125rem] leading-relaxed text-muted-foreground">
                      Used for assistant chat by default. You can still pick a different model in the chat panel.
                    </p>
                  </div>
                  <label className="block max-w-xs">
                    <span className="sr-only">Default AI model</span>
                    <LlmModelSelect
                      className="w-full"
                      value={defaultChatModel}
                      aria-label="Default AI model"
                      title="Default AI model"
                      openAiModels={OPENAI_CHAT_MODELS}
                      anthropicModels={ANTHROPIC_CHAT_MODELS}
                      onChange={(model, provider) =>
                        onDefaultChatModelChange(model as LlmModelValue, provider)
                      }
                    />
                  </label>
                </div>

                <div className="flex flex-col gap-2.5">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Writing goal (words)</h3>
                    <p className="max-w-xl text-[0.8125rem] leading-relaxed text-muted-foreground">
                      Target word count for this book. Leave blank to hide progress in the footer.
                    </p>
                  </div>
                  <label className="block max-w-xs">
                    <span className="sr-only">Writing goal (words)</span>
                    <Input
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="None"
                      disabled={!hasOpenBook}
                      value={writingGoalWords > 0 ? String(writingGoalWords) : ""}
                      aria-label="Writing goal (words)"
                      onChange={(event) => {
                        const digits = event.target.value.replace(/[^\d]/g, "");
                        onWritingGoalChange?.(normalizeWritingGoalWords(digits === "" ? 0 : digits));
                      }}
                    />
                  </label>
                </div>

                <div className="flex flex-col gap-2.5">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Book type</h3>
                    <p className="max-w-xl text-[0.8125rem] leading-relaxed text-muted-foreground">
                      Changes labels and which workbench tools are shown. Existing characters, plot, and research stay
                      on the book if you switch away from fiction.
                    </p>
                  </div>
                  <div className="max-w-xs">
                    <Select
                      value={bookType}
                      disabled={!hasOpenBook}
                      onValueChange={(value) => {
                        const next = value as BookTypeId;
                        if (next === "article") {
                          onBookTypeChange?.(next, bookStructure === "collection" ? "collection" : "single");
                          return;
                        }
                        onBookTypeChange?.(next);
                      }}
                    >
                      <SelectTrigger aria-label="Book type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {BOOK_TYPES.map((id) => (
                          <SelectItem key={id} value={id}>
                            {BOOK_TYPE_LABELS[id]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {bookType === "article" ? (
                    <div className="max-w-xs">
                      <Select
                        value={bookStructure === "collection" ? "collection" : "single"}
                        disabled={!hasOpenBook}
                        onValueChange={(value) =>
                          onBookTypeChange?.("article", value as BookStructure)
                        }
                      >
                        <SelectTrigger aria-label="Article structure">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="single">Single article</SelectItem>
                          <SelectItem value="collection">Collection of articles</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ) : null}
                </div>

                {!web ? (
                <div className="flex flex-col gap-2.5">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Your API keys</h3>
                    <p className="max-w-xl text-[0.8125rem] leading-relaxed text-muted-foreground">
                      Keys stay on this computer (encrypted with OS secure storage when available). Leave blank to use
                      app-managed / .env keys. BYOK usage does not debit your managed Just Write token allowance.
                    </p>
                  </div>
                  <div className="max-w-md space-y-3">
                    <label className="block space-y-1.5">
                      <span className="text-xs font-medium text-muted-foreground">OpenAI API key</span>
                      <Input
                        type="password"
                        autoComplete="off"
                        spellCheck={false}
                        placeholder="sk-…"
                        value={openaiApiKeyDraft}
                        aria-label="OpenAI API key"
                        onChange={(event) => onOpenaiApiKeyChange?.(event.target.value)}
                      />
                    </label>
                    <label className="block space-y-1.5">
                      <span className="text-xs font-medium text-muted-foreground">Anthropic API key</span>
                      <Input
                        type="password"
                        autoComplete="off"
                        spellCheck={false}
                        placeholder="sk-ant-…"
                        value={anthropicApiKeyDraft}
                        aria-label="Anthropic API key"
                        onChange={(event) => onAnthropicApiKeyChange?.(event.target.value)}
                      />
                    </label>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button type="button" size="sm" onClick={() => onSaveApiKeys?.()} disabled={!onSaveApiKeys}>
                        Save API keys
                      </Button>
                      {apiKeysSaved ? (
                        <span className="text-xs text-muted-foreground">Saved</span>
                      ) : null}
                    </div>
                  </div>
                </div>
                ) : (
                <div className="flex flex-col gap-2.5">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">AI models</h3>
                    <p className="max-w-xl text-[0.8125rem] leading-relaxed text-muted-foreground">
                      The web app uses Just Write server keys. Provider API keys are not stored in the browser.
                    </p>
                  </div>
                </div>
                )}

                <div className="flex flex-col gap-2.5">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Your prompts</h3>
                    <p className="max-w-xl text-[0.8125rem] leading-relaxed text-muted-foreground">
                      JustWrite always applies a built-in master style guide in the background. Add your own notes
                      below — they are appended for that feature only.
                    </p>
                  </div>

                  {USER_PROMPT_FIELDS.map((field) => (
                    <div key={field.kind} className="space-y-2">
                      <div>
                        <h4 className="text-sm font-medium text-foreground">{field.title}</h4>
                        <p className="mt-1 max-w-2xl text-xs text-muted-foreground">{field.description}</p>
                      </div>
                      <Textarea
                        aria-label={field.ariaLabel}
                        className="min-h-30 font-mono text-xs leading-relaxed"
                        placeholder="Optional — e.g. preferred tone, names to keep, what to avoid…"
                        value={userPromptDrafts[field.kind]}
                        onChange={(event) => updateUserPromptDraft(field.kind, event.target.value)}
                      />
                    </div>
                  ))}

                  <div className="flex flex-wrap gap-2">
                    <Button type="button" size="sm" onClick={onSaveUserPrompts}>
                      Save prompts
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={onClearUserPrompts}>
                      Clear all
                    </Button>
                    {userPromptsSaved ? (
                      <span className="self-center text-xs text-muted-foreground">Saved</span>
                    ) : null}
                  </div>
                </div>

                {message && section === "general" ? (
                  <p
                    className="rounded-sm border border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground"
                    role="status"
                  >
                    {message}
                  </p>
                ) : null}
              </div>
            )}

            {section === "audio" && (
              <div className="flex max-w-2xl flex-col gap-5 p-5">
                <div className="flex flex-col gap-2.5">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Book narration voices</h3>
                    <p className="max-w-xl text-[0.8125rem] leading-relaxed text-muted-foreground">
                    Choose one default voice for the whole book, or select several for multi-POV stories. The chapter
                    voice dropdown only appears when more than one voice is selected.
                  </p>
                </div>

                {!hasOpenBook ? (
                  <EmptyState
                    compact
                    title="Open a book first"
                    description="Choose narration voices after you open a book in the workbench."
                  />
                ) : ttsLoading ? (
                  <p className="text-xs text-muted-foreground">Loading available voices…</p>
                ) : ttsError ? (
                  <p className="text-xs text-destructive" role="alert">
                    {ttsError}
                  </p>
                ) : (
                  <div className="space-y-5">
                    {!hasElevenLabsVoices ? (
                      <fieldset className="space-y-2">
                        <legend className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          {PROVIDER_LABELS.elevenlabs}
                        </legend>
                        <p className="rounded-sm border border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                          ElevenLabs voices need a non-empty{" "}
                          <code className="text-[11px]">ELEVENLABS_API_KEY</code> in{" "}
                          <code className="text-[11px]">apps/web/.env</code> or{" "}
                          <code className="text-[11px]">apps/desktop-v2/.env</code>, then restart the
                          desktop app. The key is currently empty, so these voices stay unavailable.
                        </p>
                        <div className="grid max-w-xl gap-1.5 sm:grid-cols-2">
                          {DESKTOP_ELEVENLABS_VOICES.map((voice) => (
                            <div
                              key={voice.id}
                              className="flex items-center gap-2 rounded-sm border border-border px-2.5 py-2 text-sm opacity-60"
                            >
                              <Checkbox disabled />
                              <span className="min-w-0 flex-1">{voice.name}</span>
                            </div>
                          ))}
                        </div>
                      </fieldset>
                    ) : null}

                    {voicesByProvider.map(({ provider, voices }) => (
                      <fieldset key={provider} className="space-y-2">
                        <legend className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          {PROVIDER_LABELS[provider]}
                        </legend>
                        <div className="grid max-w-xl gap-1.5 sm:grid-cols-2">
                          {voices.map((voice) => {
                            const checked = audioSettings.voiceIds.includes(voice.id);
                            const previewing = previewingVoiceId === voice.id;
                            return (
                              <div
                                key={voice.id}
                                className="flex items-center gap-2 rounded-sm border border-border px-2.5 py-2 text-sm hover:bg-muted/40"
                              >
                                <label
                                  htmlFor={`voice-${voice.id}`}
                                  className="flex min-w-0 flex-1 cursor-pointer items-center gap-2"
                                >
                                  <Checkbox
                                    id={`voice-${voice.id}`}
                                    checked={checked}
                                    onCheckedChange={() => toggleBookVoice(voice.id)}
                                  />
                                  <span className="min-w-0 truncate" title={voice.id}>
                                    {voice.name}
                                  </span>
                                </label>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon-sm"
                                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-sm border border-border text-muted-foreground hover:bg-muted/60 hover:text-foreground disabled:opacity-50"
                                  aria-label={
                                    previewing ? `Playing ${voice.name}` : `Preview ${voice.name}`
                                  }
                                  title={`Preview ${voice.name}`}
                                  disabled={previewingVoiceId !== null && !previewing}
                                  onClick={() => void previewVoice(voice)}
                                >
                                  {previewing ? (
                                    <span className="text-[10px] font-medium uppercase tracking-wide">
                                      …
                                    </span>
                                  ) : (
                                    <IconPlay size={12} />
                                  )}
                                </Button>
                              </div>
                            );
                          })}
                        </div>

                        {provider === "elevenlabs" && hasElevenLabsVoices ? (
                          <div className="max-w-xl space-y-2 rounded-sm border border-border bg-muted/15 px-3 py-3">
                            <p className="text-xs font-medium text-foreground">
                              Add ElevenLabs voice by ID
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Paste a voice ID from your ElevenLabs account (Voice library → voice → ID).
                              Instant / cloned voices work the same as the presets above.
                            </p>
                            <div className="flex flex-wrap items-center gap-2">
                              <Input
                                value={customVoiceIdDraft}
                                onChange={(e) => {
                                  setCustomVoiceIdDraft(e.target.value);
                                  if (customVoiceError) setCustomVoiceError(null);
                                }}
                                placeholder="e.g. JBFqnCBsd6RMkjVDRZzb"
                                className="min-w-56 flex-1 font-mono text-xs"
                                aria-label="ElevenLabs voice ID"
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    addCustomElevenLabsVoice();
                                  }
                                }}
                              />
                              <Button
                                type="button"
                                size="sm"
                                onClick={addCustomElevenLabsVoice}
                                disabled={!hasOpenBook}
                              >
                                Add voice
                              </Button>
                            </div>
                            {customVoiceError ? (
                              <p className="text-xs text-destructive" role="alert">
                                {customVoiceError}
                              </p>
                            ) : null}
                          </div>
                        ) : null}
                      </fieldset>
                    ))}

                    {previewError ? (
                      <p className="text-xs text-destructive" role="alert">
                        {previewError}
                      </p>
                    ) : null}

                    <p className="text-xs text-muted-foreground">
                      {audioSettings.voiceIds.length === 0
                        ? "None selected — chapters use the first available provider voice as a default."
                        : audioSettings.voiceIds.length === 1
                          ? "One voice selected — used for every chapter (no dropdown)."
                          : `${audioSettings.voiceIds.length} voices selected — choose per chapter on the manuscript tab.`}
                    </p>

                    {audioSettings.voiceIds.length > 0 ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => onAudioSettingsChange({ voiceIds: [] })}
                      >
                        Clear selection
                      </Button>
                    ) : null}
                  </div>
                )}
                </div>
              </div>
            )}

            {section === "role" && (
              <div className="flex max-w-2xl flex-col gap-5 p-5">
                <div className="flex flex-col gap-2.5">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Upgrade your plan</h3>
                    <p className="max-w-xl text-[0.8125rem] leading-relaxed text-muted-foreground">
                      Writer is free to download. Author unlocks AI, formatting, beta reads, and editor invites. Editor
                      adds client books and a larger AI token allowance.
                    </p>
                    {cloudPlan ? (
                      <p className="mt-2 text-xs text-muted-foreground">
                        Current plan:{" "}
                        <span className="font-medium text-foreground">
                          {cloudPlan === "free" ? "Writer (free)" : cloudPlan === "author" ? "Author" : "Editor"}
                        </span>
                        {hasPublishPro ? (
                          <span className="ml-2 font-medium text-foreground">· Publish Pro</span>
                        ) : null}
                      </p>
                    ) : null}
                  </div>
                </div>

                {billingTokens && billingTokens.limit > 0 ? (
                  <div className="rounded-sm border border-border bg-muted/15 p-4">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <h4 className="text-sm font-semibold text-foreground">AI token usage</h4>
                      <p className="text-xs tabular-nums text-muted-foreground">
                        {formatTokenCount(billingTokens.used)} / {formatTokenCount(billingTokens.limit)} used
                      </p>
                    </div>
                    <div
                      className="mt-3 h-2 overflow-hidden rounded-full bg-muted"
                      role="meter"
                      aria-valuemin={0}
                      aria-valuemax={billingTokens.limit}
                      aria-valuenow={Math.min(billingTokens.used, billingTokens.limit)}
                      aria-label="Managed AI tokens used this month"
                    >
                      <div
                        className="h-full rounded-full bg-primary transition-[width]"
                        style={{
                          width: `${Math.min(100, (billingTokens.used / Math.max(1, billingTokens.limit)) * 100)}%`,
                        }}
                      />
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span>
                        Remaining:{" "}
                        <span className="font-medium text-foreground">
                          {formatTokenCount(billingTokens.remaining)}
                        </span>
                      </span>
                      {billingTokens.bonusRemaining > 0 ? (
                        <span>
                          Bonus:{" "}
                          <span className="font-medium text-foreground">
                            {formatTokenCount(billingTokens.bonusRemaining)}
                          </span>
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Managed cloud AI only. Desktop BYOK keys do not debit this meter.
                    </p>
                  </div>
                ) : null}

                <div className="flex flex-wrap items-center gap-2">
                  <div
                    className="inline-flex rounded-sm border border-border p-0.5"
                    role="group"
                    aria-label="Billing interval"
                  >
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "rounded-sm px-3 py-1.5 text-xs font-medium",
                        billingInterval === "month"
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                      onClick={() => setBillingInterval("month")}
                    >
                      Monthly
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "rounded-sm px-3 py-1.5 text-xs font-medium",
                        billingInterval === "year"
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                      onClick={() => setBillingInterval("year")}
                    >
                      Yearly
                      <span className="ml-1 opacity-80">· 2 months free</span>
                    </Button>
                  </div>
                  {hasStripeBilling ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={billingBusy}
                      onClick={() => void onManageBilling()}
                    >
                      Manage billing
                    </Button>
                  ) : null}
                </div>

                <div className="grid gap-3 lg:grid-cols-3">
                  <article className="flex flex-col rounded-sm border border-border bg-muted/10 p-4">
                    <h4 className="text-sm font-semibold text-foreground">Writer</h4>
                    <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                      $0
                      <span className="text-xs font-normal text-muted-foreground"> / forever</span>
                    </p>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Free to download. Write and share with editors — no AI features or beta reads.
                    </p>
                    <ul className="mt-3 flex-1 space-y-1.5 text-xs text-muted-foreground">
                      <li>Manuscript editor &amp; chapters</li>
                      <li>Share books with editors</li>
                      <li>Cloud sync when signed in</li>
                      <li>No AI tokens</li>
                    </ul>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="mt-4"
                      onClick={() => onPersonaChange("writer")}
                    >
                      {persona === "writer" ? "Using Writer layout" : "Use Writer layout"}
                    </Button>
                  </article>

                  <article
                    className={cn(
                      "flex flex-col rounded-sm border p-4",
                      cloudPlan === "author" ? "border-primary bg-primary/5" : "border-border bg-muted/10"
                    )}
                  >
                    <h4 className="text-sm font-semibold text-foreground">Author</h4>
                    <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                      ${billingInterval === "year" ? "120" : "12"}
                      <span className="text-xs font-normal text-muted-foreground">
                        {billingInterval === "year" ? " / year" : " / month"}
                      </span>
                    </p>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Create and format your own books, run beta reads, and invite editors.
                    </p>
                    <ul className="mt-3 flex-1 space-y-1.5 text-xs text-muted-foreground">
                      <li>AI writing help (500k tokens / month)</li>
                      <li>Basic PDF / EPUB export</li>
                      <li>Run beta reads</li>
                      <li>Invite an editor to your book</li>
                      <li>Publish Pro add-on for store package</li>
                      <li>Limited covers &amp; chapter audio</li>
                    </ul>
                    {cloudPlan === "author" || cloudPlan === "editor" ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="mt-4"
                        onClick={() => onPersonaChange("author")}
                      >
                        {persona === "author" ? "Using Author layout" : "Use Author layout"}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        className="mt-4"
                        disabled={billingBusy}
                        onClick={() => void onUpgrade("author")}
                      >
                        {billingBusy ? "Opening…" : "Upgrade to Author"}
                      </Button>
                    )}
                  </article>

                  <article
                    className={cn(
                      "flex flex-col rounded-sm border p-4",
                      cloudPlan === "editor" ? "border-primary bg-primary/5" : "border-border bg-muted/10"
                    )}
                  >
                    <h4 className="text-sm font-semibold text-foreground">Editor</h4>
                    <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                      ${billingInterval === "year" ? "390" : "39"}
                      <span className="text-xs font-normal text-muted-foreground">
                        {billingInterval === "year" ? " / year" : " / month"}
                      </span>
                    </p>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Client books, author invites, and Publish Pro included. Higher audio and cover quotas.
                    </p>
                    <ul className="mt-3 flex-1 space-y-1.5 text-xs text-muted-foreground">
                      <li>Everything in Author</li>
                      <li>750k AI tokens / month</li>
                      <li>Create clients &amp; import books</li>
                      <li>Accept author invites &amp; edit their books</li>
                      <li>Publish Pro included</li>
                      <li>Higher audio &amp; cover quotas</li>
                    </ul>
                    {cloudPlan === "editor" ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="mt-4"
                        onClick={() => onPersonaChange("editor")}
                      >
                        {persona === "editor" ? "Using Editor layout" : "Use Editor layout"}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        className="mt-4"
                        disabled={billingBusy}
                        onClick={() => void onUpgrade("editor")}
                      >
                        {billingBusy ? "Opening…" : "Upgrade to Editor"}
                      </Button>
                    )}
                  </article>
                </div>

                {!hasPublishPro &&
                typeof (cloudAuthDesktopV2 as { startPublishProCheckout?: unknown }).startPublishProCheckout ===
                  "function" ? (
                  <div className="flex flex-col gap-3 rounded-sm border border-border bg-muted/10 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">Publish Pro</h4>
                      <p className="mt-1 max-w-lg text-xs text-muted-foreground">
                        One-time add-on for premium packaging, cover wrap, and store ZIP delivery. Included with
                        Editor.
                      </p>
                      <p className="mt-1 text-sm font-semibold tabular-nums text-foreground">$79 once</p>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="shrink-0"
                      disabled={billingBusy}
                      onClick={() => void onBuyPublishPro()}
                    >
                      {billingBusy ? "Opening…" : "Buy Publish Pro"}
                    </Button>
                  </div>
                ) : null}

                {billingReferral ? (
                  <div className="rounded-sm border border-border bg-muted/10 p-4">
                    <h4 className="text-sm font-semibold text-foreground">Invite writers</h4>
                    <p className="mt-1 max-w-lg text-xs text-muted-foreground">
                      Share Just Write with other writers. When they subscribe, you earn bonus AI tokens.
                    </p>
                    <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                      <code className="rounded-sm border border-border bg-background px-2.5 py-1.5 font-mono text-xs">
                        {billingReferral.code}
                      </code>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => void onCopyReferralLink()}
                      >
                        {referralCopied ? "Copied" : "Copy share link"}
                      </Button>
                    </div>
                    <p className="mt-2 break-all text-xs text-muted-foreground">{billingReferral.shareUrl}</p>
                  </div>
                ) : null}

                {!isSignedIn ? (
                  <p className="text-xs text-muted-foreground">
                    Sign in under Account so checkout can attach the subscription to your Just Write user.
                  </p>
                ) : null}
                {billingMessage ? (
                  <p
                    className="rounded-sm border border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground"
                    role="status"
                  >
                    {billingMessage}
                  </p>
                ) : null}
              </div>
            )}

            {section === "clients" && showClients ? (
              <ClientsPanel embedded onOpenBookId={onOpenBookId} />
            ) : null}

            {section === "workspace" && (
              <div className="flex max-w-2xl flex-col gap-5 p-5">
                <div className="flex flex-col gap-2.5">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Editor client folders</h3>
                    <p className="max-w-xl text-[0.8125rem] leading-relaxed text-muted-foreground">
                    When you open a client&apos;s book from the cloud, Just Write can save under{" "}
                    <code className="text-xs">Clients / Client Name / Book / Version</code>. Pick a root folder
                    (for example <code className="text-xs">~/JustWrite/Clients</code>).
                  </p>
                </div>
                <p className="rounded-sm border border-border bg-muted/20 px-3 py-2 font-mono text-xs break-all">
                  {workspaceRoot || "Not set — Save As will ask each time"}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" onClick={() => void onPickWorkspace()}>
                    Choose folder…
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={onClearWorkspace}
                    disabled={!workspaceRoot}
                  >
                    Clear
                  </Button>
                  {workspaceSaved ? (
                    <span className="self-center text-xs text-muted-foreground">Saved</span>
                  ) : null}
                </div>
                </div>
              </div>
            )}

            {section === "account" && (
              <div className="flex max-w-2xl flex-col gap-5 p-5">
                <div className="flex flex-col gap-2.5">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">
                      {accountPrimary ? "Account" : "Account & cloud"}
                    </h3>
                    <p className="max-w-xl text-[0.8125rem] leading-relaxed text-muted-foreground">
                    {accountPrimary
                      ? "Sign in with the same Just Write account you use on heyjustwrite.com. Your books save to your account. On desktop, a local backup is kept for offline use and syncs when you're back online."
                      : web
                      ? "Sign in with the same Just Write account you use on heyjustwrite.com. No pairing code is required in the browser."
                      : "Sign in through heyjustwrite.com in your system browser, then approve this computer. Your book file always saves locally; when you're connected, saving also updates your online library."}
                    </p>
                  </div>

                {isSignedIn ? (
                  <div className="space-y-4">
                    <div className="flex flex-col gap-4 rounded-sm border border-border bg-muted/15 p-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex min-w-0 gap-3">
                        <div
                          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-border bg-muted text-sm font-medium text-muted-foreground"
                          aria-hidden
                        >
                          {(userLabel || "?").slice(0, 1).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-foreground">{userLabel || "Signed in"}</p>
                          <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2 py-0.5 font-medium text-primary">
                              <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />
                              {accountPrimary ? "Account connected" : "Cloud backup ready"}
                            </span>
                            <span className="text-muted-foreground">Web session</span>
                          </p>
                          <p className="mt-2 text-xs text-muted-foreground">
                            {accountPrimary
                              ? "Each save updates your account when you're online. On desktop, a local backup is kept as well."
                              : "Each save keeps a local copy and can refresh your online copy when you're online."}
                          </p>
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-col gap-2 self-start sm:items-end">
                        {hasStripeBilling ? (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={billingBusy}
                            onClick={() => void onManageBilling()}
                          >
                            Manage billing
                          </Button>
                        ) : null}
                        <Button variant="outline" size="sm" onClick={onSignOut}>
                          Sign out
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Button size="sm" onClick={() => void onWebSignIn()} disabled={busy || !webCloudConfigured}>
                      {busy ? "Working…" : "Sign in with Just Write"}
                    </Button>
                    {!webCloudConfigured ? (
                      <p className="max-w-md text-xs text-destructive">
                        Set DESKTOP_WEB_APP_URL in apps/desktop-v2/.env (or NEXTAUTH_URL in apps/web/.env), then
                        restart the app.
                      </p>
                    ) : (
                      <p className="max-w-md text-xs text-muted-foreground">
                        {web
                          ? "You'll continue through heyjustwrite.com using your existing web session when available."
                          : "Your browser opens the pairing page. Sign in on heyjustwrite.com if prompted, then approve this computer. Keep this window open until connection succeeds."}
                      </p>
                    )}
                  </div>
                )}

                {message && section === "account" ? (
                  <p
                    className="rounded-sm border border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground"
                    role="status"
                  >
                    {message}
                  </p>
                ) : null}
                </div>
              </div>
            )}

            {section === "backup" && (
              <div className="flex max-w-2xl flex-col gap-5 p-5">
                <div className="flex flex-col gap-2.5">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Backup folder</h3>
                    <p className="max-w-xl text-[0.8125rem] leading-relaxed text-muted-foreground">
                      Books save to your account. This folder is a local backup for offline work and recovery.
                    </p>
                  </div>
                  <p className="rounded-sm border border-border bg-muted/20 px-3 py-2 font-mono text-xs break-all">
                    {backupFolder || "Documents/Just Write/Backups"}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        void (async () => {
                          const next = await chooseBackupFolder();
                          if (!next) return;
                          setBackupFolder(next);
                          setBackupSaved(true);
                        })();
                      }}
                    >
                      Choose folder…
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        writeBackupFolderOverride(null);
                        void defaultBackupFolder()
                          .then((path) => {
                            setBackupFolder(path);
                            setBackupSaved(true);
                          })
                          .catch(() => {
                            setBackupFolder("");
                            setBackupSaved(true);
                          });
                      }}
                    >
                      Reset to default
                    </Button>
                    {backupSaved ? (
                      <span className="self-center text-xs text-muted-foreground">Saved</span>
                    ) : null}
                  </div>
                </div>
              </div>
            )}

            {section === "about" && (
              <div className="flex max-w-2xl flex-col gap-5 p-5">
                <div className="flex flex-col gap-2.5">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">About</h3>
                    <p className="max-w-xl text-[0.8125rem] leading-relaxed text-muted-foreground">
                      JustWrite {web ? "on the web" : "for desktop"} — early access.
                    </p>
                  </div>
                </div>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between gap-4 border-b border-border py-2">
                    <dt className="text-muted-foreground">Version</dt>
                    <dd className="font-mono text-xs text-foreground">
                      {versionInfo ? formatVersionLabel(versionInfo) : "…"}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4 border-b border-border py-2">
                    <dt className="text-muted-foreground">Git</dt>
                    <dd className="break-all font-mono text-xs text-foreground">
                      {versionInfo?.gitSha ?? "…"}
                    </dd>
                  </div>
                  {versionInfo?.builtAt ? (
                    <div className="flex justify-between gap-4 border-b border-border py-2">
                      <dt className="text-muted-foreground">Built</dt>
                      <dd className="font-mono text-xs text-foreground">{versionInfo.builtAt}</dd>
                    </div>
                  ) : null}
                  <div className="flex justify-between gap-4 border-b border-border py-2">
                    <dt className="text-muted-foreground">Channel</dt>
                    <dd className="font-mono text-xs text-foreground">
                      {versionInfo?.channel ?? "…"}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4 border-b border-border py-2">
                    <dt className="text-muted-foreground">Product</dt>
                    <dd className="text-foreground">JustWrite</dd>
                  </div>
                </dl>
              </div>
            )}
          </main>
        </div>
      </Card>
    </section>
  );
}

/** Starts sign-in from outside Settings (e.g. Open from cloud). */
export async function startWebSignInFromSettingsFlow(): Promise<void> {
  await signInForCurrentRuntime();
}
